import { useRef, useState, type FunctionComponent } from "react";
import { DeleteOutlined, PictureOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Image, Slider, Space, Typography, Upload, message } from "antd";
import ImgCrop from "antd-img-crop";
import { type RcFile } from "antd/es/upload";

import {
  clampCoverPosition,
  coverPositionStyle,
  DEFAULT_CHALLENGE_COVER_POSITION,
} from "@fitness-app/data-models/entities/Challenge";
import { getParentTrainerId } from "@fitness-app/app-store/src/store/reducers/user/selectors";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import { supabase, useAppSelector } from "~/store/initializeStore";
import ChallengeCoverAiGenerator, { type ChallengeCoverContext } from "./ChallengeCoverAiGenerator";

// TRE-1583 — challenge cover editor: manual upload OR the AI generator, with preview + removal.
// Used by the create drawer and (inline) on the challenge detail page.
// The cover is shown in mobile as a wide horizontal banner, so the preview tile is 16:9 and
// uploads go through a 16:9 crop — a portrait file can't end up as a cover anymore.

const COVER_WIDTH = 320;
const COVER_HEIGHT = 180;
// The two shapes the cover is actually rendered in: the list cards are ~2.8:1 (they cut top/bottom
// of a 16:9 source) and the hero sits around 16:9 but goes narrower as its content grows.
const CARD_PREVIEW_WIDTH = 200;
const CARD_PREVIEW_HEIGHT = 72;
const HERO_PREVIEW_WIDTH = 176;
const HERO_PREVIEW_HEIGHT = 108;
const MAX_UPLOAD_MB = 8;

type Props = {
  challengeId: string;
  challengeContext: ChallengeCoverContext;
  value: string | null;
  onChange: (url: string | null) => void;
  /** TRE-1655 — focal point (0–100 per axis), CSS object-position semantics. */
  positionX?: number;
  positionY?: number;
  onPositionChange?: (positionX: number, positionY: number) => void;
};

const ChallengeCoverField: FunctionComponent<Props> = ({
  challengeId,
  challengeContext,
  value,
  onChange,
  positionX,
  positionY,
  onPositionChange,
}) => {
  const parentId = useAppSelector(getParentTrainerId);
  const [uploading, setUploading] = useState(false);
  // Dragging updates the previews locally; the save fires once on release (onChangeComplete).
  const [draftX, setDraftX] = useState<number | null>(null);
  const [draftY, setDraftY] = useState<number | null>(null);
  const focalX = draftX ?? clampCoverPosition(positionX);
  const focalY = draftY ?? clampCoverPosition(positionY);
  const focalStyle = coverPositionStyle(focalX, focalY);
  // Bumped on every cover choice (upload start, delete, AI pick) — a finished upload only applies
  // when it is still the latest choice, so a slow request can't overwrite a newer action.
  const choiceTokenRef = useRef(0);

  const applyChange = (url: string | null) => {
    choiceTokenRef.current += 1;
    onChange(url);
  };

  const uploadCover = async (file: RcFile): Promise<void> => {
    if (file.size / 1024 / 1024 > MAX_UPLOAD_MB) {
      void message.error(`Zdjęcie może mieć maksymalnie ${MAX_UPLOAD_MB}MB`);
      return;
    }

    const token = ++choiceTokenRef.current;
    setUploading(true);
    try {
      const extension = file.type === "image/png" ? "png" : "jpg";
      const path = `${parentId}/challenges/covers/${challengeId}_${generateUniqId()}.${extension}`;

      const { error } = await supabase.storage.from("shared").upload(path, file, {
        cacheControl: "3600",
        upsert: true,
      });

      if (error) {
        throw new Error(error.message);
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from("shared").getPublicUrl(path, {
        transform: { width: 800, resize: "contain" },
      });

      if (token === choiceTokenRef.current) {
        onChange(publicUrl);
      }
    } catch (error) {
      void message.error(error instanceof Error ? error.message : "Nie udało się wgrać okładki");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Space direction="vertical" size={8}>
      {value ? (
        // Two previews, because ONE cannot show the effect: the stored cover is 16:9, so in a 16:9
        // box `cover` crops nothing and the sliders look dead. The card strip is wider than the
        // source (crops top/bottom) and the hero can be narrower (crops the sides) — between them
        // every pixel the trainer can lose is visible.
        <Space size={12} align="start" wrap>
          <Space direction="vertical" size={4}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Karta na liście
            </Typography.Text>
            <img
              src={value}
              alt=""
              width={CARD_PREVIEW_WIDTH}
              height={CARD_PREVIEW_HEIGHT}
              style={{
                borderRadius: 10,
                height: CARD_PREVIEW_HEIGHT,
                objectFit: "cover",
                objectPosition: focalStyle,
                width: CARD_PREVIEW_WIDTH,
              }}
            />
          </Space>
          <Space direction="vertical" size={4}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Nagłówek wyzwania
            </Typography.Text>
            <Image
              src={value}
              width={HERO_PREVIEW_WIDTH}
              height={HERO_PREVIEW_HEIGHT}
              style={{ objectFit: "cover", objectPosition: focalStyle, borderRadius: 10 }}
              preview={{ mask: "Podgląd" }}
            />
          </Space>
        </Space>
      ) : (
        <div
          className="flex items-center justify-center"
          style={{
            width: COVER_WIDTH,
            height: COVER_HEIGHT,
            borderRadius: 12,
            border: "1px dashed #d9d9d9",
            background: "#fafafa",
          }}
        >
          <Space direction="vertical" size={4} align="center">
            <PictureOutlined style={{ fontSize: 24, color: "#bfbfbf" }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Brak okładki — karta użyje domyślnego tła
            </Typography.Text>
          </Space>
        </div>
      )}

      {value && onPositionChange ? (
        <div style={{ maxWidth: 360 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Kadr — okładka jest zapisana w 16:9, ale karty w apce są szersze, a nagłówek bywa węższy. Ustaw, co musi
            przetrwać przycięcie.
          </Typography.Text>
          <div className="mt-2 grid grid-cols-[56px_1fr] items-center gap-x-3">
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Poziom
            </Typography.Text>
            <Slider
              min={0}
              max={100}
              step={1}
              value={focalX}
              tooltip={{ formatter: (position) => (position === 50 ? "środek" : `${position ?? 50}%`) }}
              marks={{ 0: "lewo", 50: "środek", 100: "prawo" }}
              onChange={setDraftX}
              onChangeComplete={(next) => {
                setDraftX(null);
                onPositionChange(clampCoverPosition(next), focalY);
              }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Pion
            </Typography.Text>
            <Slider
              min={0}
              max={100}
              step={1}
              value={focalY}
              tooltip={{ formatter: (position) => (position === 50 ? "środek" : `${position ?? 50}%`) }}
              marks={{ 0: "góra", 50: "środek", 100: "dół" }}
              onChange={setDraftY}
              onChangeComplete={(next) => {
                setDraftY(null);
                onPositionChange(focalX, clampCoverPosition(next));
              }}
            />
          </div>
        </div>
      ) : null}

      <Space size={8} wrap>
        <ImgCrop aspect={COVER_WIDTH / COVER_HEIGHT} rotationSlider={false} modalTitle="Przytnij okładkę (16:9)">
          <Upload
            accept="image/png,image/jpeg,image/webp"
            maxCount={1}
            showUploadList={false}
            customRequest={({ file }) => void uploadCover(file as RcFile)}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              {value ? "Zmień zdjęcie" : "Prześlij zdjęcie"}
            </Button>
          </Upload>
        </ImgCrop>
        <ChallengeCoverAiGenerator
          challengeId={challengeId}
          challengeContext={challengeContext}
          setCoverUrl={applyChange}
        />
        {value ? (
          <Button
            danger
            type="text"
            icon={<DeleteOutlined />}
            disabled={uploading}
            onClick={() => {
              applyChange(null);
              // A fresh cover starts centred — keeping the old framing would silently mis-crop it.
              onPositionChange?.(DEFAULT_CHALLENGE_COVER_POSITION, DEFAULT_CHALLENGE_COVER_POSITION);
            }}
          >
            Usuń
          </Button>
        ) : null}
      </Space>
    </Space>
  );
};

export default ChallengeCoverField;
